import React, { useState } from "react";
import { X, Plus, Loader2, MessageCircle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import AssignTaskModal from "./AssignTaskModal";

/**
 * "+ Create Task" (Command Center) — C-ORE Task + Playbook + WhatsApp
 * Execution System (2026-08). A founder can create an arbitrary task with
 * no C-ORE rule behind it, optionally with a Campaign Playbook, in one
 * session. The created task immediately becomes a live Command Center
 * Execution Plan item — same lifecycle as every C-ORE-generated task,
 * distinguished only by "Founder created" metadata (never a second
 * task system). "Save" is followed straight by "Assign Task" so the
 * whole delegation flow — create → assign → WhatsApp — fits one sitting.
 */
const PRIORITY_OPTIONS = [
  { value: 1, label: "High" },
  { value: 3, label: "Medium" },
  { value: 5, label: "Low" },
];

export default function CreateTaskModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState(3);
  const [dueDate, setDueDate] = useState("");
  const [withPlaybook, setWithPlaybook] = useState(false);
  const [objective, setObjective] = useState("");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [channels, setChannels] = useState("");
  const [checklist, setChecklist] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // { rule_id, title }
  const [showAssign, setShowAssign] = useState(false);

  const reset = () => {
    setTitle(""); setDescription(""); setPriority(3); setDueDate("");
    setWithPlaybook(false); setObjective(""); setAudience(""); setOffer("");
    setChannels(""); setChecklist(""); setError(""); setCreated(null); setShowAssign(false);
  };

  const close = () => { reset(); onClose(); };

  if (!open) return null;

  const submit = async () => {
    if (!title.trim()) { setError("Please enter a task title."); return; }
    setSaving(true);
    setError("");
    const base = { title: title.trim(), description: description.trim() || null, priority, dueDate: dueDate || null };
    const res = withPlaybook
      ? await api.createFounderTaskWithPlaybook({
          ...base,
          playbook: {
            objective: objective.trim() || null,
            audience: audience.trim() || null,
            offer: offer.trim() || null,
            channels: channels.split(",").map((c) => c.trim()).filter(Boolean),
            checklist: checklist.split("\n").map((c) => c.trim()).filter(Boolean),
          },
        })
      : await api.createFounderTask(base);
    setSaving(false);
    if (!res.ok) { setError(res.error || "Could not save this task. Please try again."); return; }
    const ruleId = res.data?.rule_id;
    setCreated({ rule_id: ruleId, title: title.trim(), priority, detail: description.trim(), due_timeline: dueDate || null });
    onCreated?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/50 backdrop-blur-sm px-4"
         data-testid="create-task-modal" onClick={close}>
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-modal max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute right-4 top-4 text-ink-400 hover:text-ink-600" data-testid="create-task-close">
          <X className="h-5 w-5" />
        </button>

        {!created ? (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-ink-100">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-800">
                <Plus className="h-3.5 w-3.5" /> Create Task
              </div>
              <h2 className="mt-1 text-[16px] font-display font-bold text-blue-800">
                Add a task for your team — with or without a Playbook.
              </h2>
            </div>

            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Title *</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Call supplier regarding delayed shipment"
                  className="mt-1 w-full rounded-lg border border-ink-200 p-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                  data-testid="create-task-title" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                  className="mt-1 w-full rounded-lg border border-ink-200 p-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-800/30 resize-none"
                  data-testid="create-task-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Priority</label>
                  <select value={priority} onChange={(e) => setPriority(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-ink-200 p-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                    data-testid="create-task-priority">
                    {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Due date</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-ink-200 p-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                    data-testid="create-task-due-date" />
                </div>
              </div>

              <label className="flex items-center gap-2 pt-1 text-[12.5px] font-medium text-ink-700">
                <input type="checkbox" checked={withPlaybook} onChange={(e) => setWithPlaybook(e.target.checked)}
                  className="h-3.5 w-3.5 accent-blue-800" data-testid="create-task-with-playbook" />
                Add a Campaign Playbook (objective, audience, offer, channels, checklist)
              </label>

              {withPlaybook && (
                <div className="space-y-2.5 rounded-lg border border-ink-100 bg-ink-50/60 p-3" data-testid="create-task-playbook-fields">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Objective</label>
                    <input value={objective} onChange={(e) => setObjective(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-ink-200 p-2 text-[13px] bg-white" data-testid="create-task-objective" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Audience</label>
                    <input value={audience} onChange={(e) => setAudience(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-ink-200 p-2 text-[13px] bg-white" data-testid="create-task-audience" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Offer</label>
                    <input value={offer} onChange={(e) => setOffer(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-ink-200 p-2 text-[13px] bg-white" data-testid="create-task-offer" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Channels (comma-separated)</label>
                    <input value={channels} onChange={(e) => setChannels(e.target.value)}
                      placeholder="WhatsApp, Email, Instagram"
                      className="mt-1 w-full rounded-lg border border-ink-200 p-2 text-[13px] bg-white" data-testid="create-task-channels" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Checklist (one item per line)</label>
                    <textarea value={checklist} onChange={(e) => setChecklist(e.target.value)} rows={3}
                      placeholder={"Prepare creative\nGet customer approval\nLaunch campaign"}
                      className="mt-1 w-full rounded-lg border border-ink-200 p-2 text-[13px] bg-white resize-none" data-testid="create-task-checklist" />
                  </div>
                </div>
              )}

              {error && <p className="text-[12px] text-status-critical-text" data-testid="create-task-error">{error}</p>}
            </div>

            <div className="px-6 pb-6 pt-1 flex items-center gap-2">
              <button onClick={submit} disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-800 px-4 py-3 text-[13px] font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
                data-testid="create-task-submit">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? "Saving…" : withPlaybook ? "Save Task & Playbook" : "Save Task"}
              </button>
              <button onClick={close} className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-3 text-[13px] font-medium text-ink-500 hover:text-ink-700" data-testid="create-task-cancel">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="px-6 py-8 text-center" data-testid="create-task-success">
            <CheckCircle2 className="mx-auto h-8 w-8 text-status-onTrack" />
            <p className="mt-3 text-[15px] font-display font-bold text-blue-800">Task saved.</p>
            <p className="mt-1 text-[13px] text-ink-500">"{created.title}" is now on your Command Center Execution Plan.</p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button onClick={close}
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 px-4 py-2.5 text-[13px] font-medium text-ink-600 hover:bg-ink-50"
                data-testid="create-task-done">Done</button>
              <button onClick={() => setShowAssign(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-status-onTrack px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-status-onTrack-text"
                data-testid="create-task-assign-now">
                <MessageCircle className="h-4 w-4" /> Assign Task
              </button>
            </div>
          </div>
        )}
      </div>
      {showAssign && created && (
        <AssignTaskModal item={created} onClose={() => close()} />
      )}
    </div>
  );
}
